/**
 * B549 (7) — the hosted quote page's controller: opens the link through the
 * PUBLIC door (group.quote.open), presses ACCEPT & PAY through its twin
 * (group.quote.respond), and hands the organiser to the provider's secure
 * page when the answer carries one. No session, no guest mint: the signed
 * token in the fragment is the whole credential.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { ApiError, createApiClient } from "../lib/api.js";
import { createPaymentGateway, loadStripeFactory, type PaymentGateway } from "../lib/gateway.js";
import { documentPaymentAppearance } from "../lib/paymentAppearance.js";
import { refusalWords, respondOutcome, tenantOfQuoteToken, type QuotePageFace, type QuotePayBy } from "../lib/quotePage.js";
import { QuotePage } from "./quotePage.js";

export function QuoteApp(props: { readonly token: string; readonly apiBaseUrl: string; readonly go: (url: string) => void }): ReactElement {
  const tenantId = tenantOfQuoteToken(props.token);
  const api = useMemo(() => (tenantId === undefined ? undefined : createApiClient({ baseUrl: props.apiBaseUrl, tenantId })), [props.apiBaseUrl, tenantId]);
  const [page, setPage] = useState<QuotePageFace | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(api !== undefined);
  const [busy, setBusy] = useState(false);
  const [payBy, setPayBy] = useState<QuotePayBy>("CARD");
  const [error, setError] = useState<{ message: string; reference?: string | undefined } | undefined>(undefined);
  /* B549 (7c): the provider's card element, mounted in the frame's card well once the invoice's payment exists. */
  const [card, setCard] = useState<"mounted" | "confirming" | { unavailable: string; fallbackUrl?: string } | undefined>(undefined);
  const gateway = useRef<PaymentGateway | undefined>(undefined);
  useEffect(() => {
    if (card === "mounted") gateway.current?.mount("#qp-card-element");
  }, [card]);

  const fail = useCallback(
    (e: unknown) => {
      const status = e instanceof ApiError ? e.status : undefined;
      const message = e instanceof Error ? e.message : "Something went wrong opening this quote. Please try again.";
      setError({ message: refusalWords({ ...(status !== undefined ? { status } : {}), message }, page?.seller), reference: e instanceof ApiError ? e.requestId : undefined });
    },
    [page?.seller],
  );

  const open = useCallback(
    (isGone: () => boolean) => {
      if (api === undefined) return;
      setLoading(true);
      setError(undefined);
      void api
        .call("group.quote.open", { token: props.token })
        .then((body) => {
          if (!isGone()) setPage((body as { page?: QuotePageFace }).page);
        })
        .catch((e: unknown) => {
          if (!isGone()) fail(e);
        })
        .finally(() => {
          if (!isGone()) setLoading(false);
        });
    },
    [api, props.token, fail],
  );

  useEffect(() => {
    let gone = false;
    open(() => gone);
    return () => {
      gone = true;
    };
    // the page opens once per token — `open` changes identity only with the refusal's wording
  }, [api, props.token]); // eslint-disable-line react-hooks/exhaustive-deps

  /* After a confirmed card payment the provider's `invoice.paid` reaches the
     server on its own clock: re-open a few times until the page reads PAID. */
  const awaitPaid = useCallback(async () => {
    if (api === undefined) return;
    for (let n = 0; n < 10; n += 1) {
      await new Promise((r) => setTimeout(r, 3_000));
      const body = (await api.call("group.quote.open", { token: props.token }).catch(() => undefined)) as { page?: QuotePageFace } | undefined;
      if (body?.page !== undefined) setPage(body.page);
      if (body?.page?.state === "PAID") {
        setCard(undefined);
        return;
      }
    }
  }, [api, props.token]);

  const respond = useCallback(() => {
    if (api === undefined || busy) return;
    setBusy(true);
    setError(undefined);
    /* The card is mounted: this press confirms the invoice's own payment with the provider. */
    if (card === "mounted" && gateway.current !== undefined) {
      void gateway.current.confirm().then((out) => {
        setBusy(false);
        if (!out.ok) {
          setError({ message: out.message });
          return;
        }
        setCard("confirming");
        void awaitPaid();
      });
      return;
    }
    void api
      .call("group.quote.respond", { token: props.token, payBy })
      .then(async (body) => {
        const next = respondOutcome(body as Parameters<typeof respondOutcome>[0]);
        if (next.kind === "pay") {
          props.go(next.url);
          return;
        }
        setPage(next.page);
        if (next.kind === "inline") {
          const built = createPaymentGateway({
            factory: await loadStripeFactory(),
            publishableKey: next.pay.publishableKey,
            ...(next.pay.stripeAccount !== undefined ? { stripeAccount: next.pay.stripeAccount } : {}),
            clientSecret: next.pay.clientSecret,
            appearance: documentPaymentAppearance(),
          });
          if (built.gateway !== undefined) {
            gateway.current = built.gateway;
            setCard("mounted");
          } else {
            setCard({ unavailable: built.unavailable, ...(next.fallbackUrl !== undefined ? { fallbackUrl: next.fallbackUrl } : {}) });
          }
        }
        setBusy(false);
      })
      .catch((e: unknown) => {
        fail(e);
        setBusy(false);
      });
  }, [api, busy, payBy, props, fail, card, awaitPaid]);

  return <QuotePage page={page} now={Date.now()} payBy={payBy} onPayBy={setPayBy} onRespond={respond} onReopen={() => open(() => false)} loading={loading} card={card} busy={busy} error={error} />;
}
